import { useState } from 'react'
import { Check, ArrowRight, ArrowLeft, Workflow as WorkflowIcon, Sparkles } from 'lucide-react'
import { useOpsCenterStore } from '@features/dashboards/components/OpsCenterMockup/store'
import { appendStep } from '@features/dashboards/components/OpsCenterMockup/components/taskOps'
import type { StepTask } from '@features/dashboards/components/OpsCenterMockup/types'
import { inputCls, labelCls } from '../styles'
import { StageGuidedShell } from './StageGuidedShell'
import type { GuidedStep } from './StageGuidedShell'
import { WORKFLOW_COMPONENTS, buildComponentParameters, buildKycCascadeTasks } from '../workflowComponentData'
import type { WorkflowComponentType } from '../workflowComponentData'
import { newEmptyWorkflow } from './WorkflowBuilderStage'

interface ComponentConfig {
  provider: string
  /** KYC only — the fallback order (try providers[0], else providers[1], ...). */
  providers: string[]
  secondary: string
}

interface WorkflowBuilderGuidedViewProps {
  accountId: string
  onWorkflowCreated: (workflowId: string) => void
}

export function WorkflowBuilderGuidedView({ accountId, onWorkflowCreated }: WorkflowBuilderGuidedViewProps) {
  const addWorkflow = useOpsCenterStore((s) => s.addWorkflow)
  const [selected, setSelected] = useState<WorkflowComponentType[]>([])
  const [config, setConfig] = useState<Record<string, ComponentConfig>>({})
  const [workflowName, setWorkflowName] = useState('')

  function toggleComponent(type: WorkflowComponentType) {
    setSelected((prev) => {
      if (prev.includes(type)) return prev.filter((t) => t !== type)
      const def = WORKFLOW_COMPONENTS.find((c) => c.type === type)!
      const firstProvider = def.providerOptions[0]?.value ?? ''
      setConfig((c) => ({
        ...c,
        [type]: { provider: firstProvider, providers: firstProvider ? [firstProvider] : [], secondary: def.secondarySuggestions[0] ?? '' },
      }))
      return [...prev, type]
    })
  }

  function updateConfig(type: WorkflowComponentType, patch: Partial<ComponentConfig>) {
    setConfig((c) => ({ ...c, [type]: { ...c[type], ...patch } }))
  }

  function toggleCascadeProvider(type: WorkflowComponentType, provider: string) {
    setConfig((c) => {
      const cur = c[type]?.providers ?? []
      const next = cur.includes(provider) ? cur.filter((p) => p !== provider) : [...cur, provider]
      return { ...c, [type]: { ...c[type], providers: next } }
    })
  }

  function createWorkflow() {
    const wf = newEmptyWorkflow(accountId, workflowName.trim())
    let tasks = wf.tasks
    for (const type of selected) {
      const cfg = config[type] ?? { provider: '', providers: [], secondary: '' }
      if (type === 'KYC') {
        tasks = [...tasks, ...buildKycCascadeTasks(cfg.providers, cfg.secondary)]
        continue
      }
      tasks = appendStep(tasks, type)
      const newTask = tasks[tasks.length - 1] as StepTask
      tasks = tasks.map((t) => (t.id === newTask.id && t.configuration.type === 'STEP'
        ? { ...t, configuration: { ...t.configuration, parameters: buildComponentParameters(type, cfg.provider, cfg.secondary) } }
        : t
      ))
    }
    addWorkflow(accountId, { ...wf, tasks })
    onWorkflowCreated(wf.id)
  }

  const steps: GuidedStep[] = [
    {
      label: 'Overview',
      render: ({ next }) => (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <WorkflowIcon size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Build a Workflow</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Pick the checks this customer needs — we&apos;ll suggest sensible provider and ruleset settings for each one, then drop them straight into the visual editor.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      ),
    },
    {
      label: 'Pick components',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Which checks does this workflow need?</h3>
            <p className="text-[12px] text-[#475569] mt-1">Select everything this customer&apos;s onboarding should run — you can add more later in the editor.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {WORKFLOW_COMPONENTS.map((def) => {
              const active = selected.includes(def.type)
              const Icon = def.icon
              return (
                <button
                  key={def.type}
                  onClick={() => toggleComponent(def.type)}
                  className="text-left rounded-[14px] p-4 transition-all flex items-start gap-3"
                  style={active
                    ? { background: def.tint, border: `1px solid ${def.color}` }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb' }
                  }
                >
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: active ? '#ffffff' : '#ffffff', border: `1px solid ${active ? def.color : '#e5e7eb'}` }}>
                    <Icon size={16} style={{ color: def.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[12px] font-semibold text-[#1e293b]">{def.label}</p>
                      {active && <Check size={13} className="shrink-0" style={{ color: def.color }} />}
                    </div>
                    <p className="text-[11px] text-[#475569] mt-0.5 leading-snug">{def.description}</p>
                  </div>
                </button>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={selected.length === 0} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Suggested config',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Suggested configuration</h3>
            <p className="text-[12px] text-[#475569] mt-1">We&apos;ve pre-filled sensible defaults — adjust anything before it&apos;s added to the workflow.</p>
          </div>
          <div className="space-y-3">
            {selected.map((type) => {
              const def = WORKFLOW_COMPONENTS.find((c) => c.type === type)!
              const cfg = config[type] ?? { provider: '', providers: [], secondary: '' }
              const Icon = def.icon
              const isKyc = type === 'KYC'
              return (
                <div key={type} className="rounded-[14px] p-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={14} style={{ color: def.color }} />
                    <p className="text-[12px] font-semibold text-[#1e293b]">{def.label}</p>
                  </div>

                  {isKyc && (
                    <div className="mb-3">
                      <label className={labelCls}>Cascade — providers tried in order until one passes</label>
                      <div className="space-y-1.5">
                        {def.providerOptions.map((o) => {
                          const idx = cfg.providers.indexOf(o.value)
                          const checked = idx !== -1
                          return (
                            <label
                              key={o.value}
                              className="flex items-center gap-2.5 px-3 py-2 rounded-[8px] cursor-pointer"
                              style={checked ? { background: def.tint, border: `1px solid ${def.color}` } : { background: '#f8fafc', border: '1px solid #e5e7eb' }}
                            >
                              <input type="checkbox" checked={checked} onChange={() => toggleCascadeProvider(type, o.value)} style={{ accentColor: def.color }} />
                              <span className="text-[11px] font-medium text-[#1e293b] flex-1">{o.label}</span>
                              {checked && <span className="text-[9px] font-mono font-bold" style={{ color: def.color }}>#{idx + 1}</span>}
                            </label>
                          )
                        })}
                      </div>
                      <p className="text-[10px] text-[#64748b] mt-1.5">Check order = cascade order. If the first provider doesn&apos;t return a match, the next one runs.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {!isKyc && (
                    <div>
                      <label className={labelCls}>{def.providerFieldLabel}</label>
                      {def.providerOptions.length > 0 ? (
                        <select className={inputCls} value={cfg.provider} onChange={(e) => updateConfig(type, { provider: e.target.value })}>
                          {def.providerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <input className={inputCls} value={cfg.provider} onChange={(e) => updateConfig(type, { provider: e.target.value })} placeholder="e.g. internal_blocklist" />
                      )}
                    </div>
                    )}
                    <div className={isKyc ? 'col-span-2' : undefined}>
                      <label className={labelCls}>{def.secondaryFieldLabel}</label>
                      <input className={inputCls} value={cfg.secondary} onChange={(e) => updateConfig(type, { secondary: e.target.value })} />
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {def.secondarySuggestions.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateConfig(type, { secondary: s })}
                            className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                            style={cfg.secondary === s ? { background: def.tint, color: def.color } : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#64748b' }}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-2 leading-snug">{def.secondaryFieldHint}</p>
                </div>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button
              onClick={next}
              disabled={selected.includes('KYC') && (config.KYC?.providers.length ?? 0) === 0}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40"
              style={{ background: '#6366f1' }}
            >
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Review',
      render: ({ back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Review & create</h3>
            <p className="text-[12px] text-[#475569] mt-1">This builds a new workflow with these steps in order, then opens it in the visual editor.</p>
          </div>
          <div className="rounded-[14px] p-5 space-y-3" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
            <div>
              <label className={labelCls}>Workflow name</label>
              <input className={inputCls} value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} placeholder="e.g. AUS-Custom-Onboarding" />
            </div>
            <div>
              <label className={labelCls}>Steps · {selected.length}</label>
              <div className="flex flex-wrap gap-1.5">
                {selected.map((type, i) => {
                  const def = WORKFLOW_COMPONENTS.find((c) => c.type === type)!
                  const cfg = config[type]
                  const providerSummary = type === 'KYC' ? cfg?.providers.join(' → ') : cfg?.provider
                  return (
                    <span key={type} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded" style={{ background: def.tint, color: def.color }}>
                      {i + 1}. {def.type}{providerSummary ? ` · ${providerSummary}` : ''}
                    </span>
                  )
                })}
              </div>
            </div>
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button
              onClick={createWorkflow}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-[9px] text-[13px] font-semibold text-white"
              style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
            >
              <Sparkles size={13} /> Create workflow & open editor
            </button>
          </div>
        </div>
      ),
    },
  ]

  return <StageGuidedShell steps={steps} />
}
